// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2026 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import parseQueryString from "@insite/client-framework/Common/Utilities/parseQueryString";
import { getDealersDataView, getDealerState } from "@insite/client-framework/Store/Data/Dealers/DealersSelectors";
import { getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import { ActiveState, Jsonld, SeoProps } from "@stoneage/App/Widgets/SearchEngineOptimization/SearchEngineOptimization";

/**
 * Activate structure based on condition
 *
 * @param SeoProps
 * @returns ActiveState
 */
const activate = ({ state, fields, currentPage, payload }: SeoProps): ActiveState => {
    const locationPage = currentPage === "Locations";
    const detailsPage = currentPage === "Dealer Details";
    payload.dealers = locationPage && getDealersDataView(state, state.pages.locationFinder.getDealersParameter)?.value;
    if (detailsPage) {
        const location = getLocation(state);
        const parsedQuery = parseQueryString<{ id?: string; invite?: string }>(location.search);
        const id = parsedQuery.id;
        const dealer = getDealerState(state, id);
        if (!dealer.isLoading && dealer?.value) {
            payload.dealers = [dealer?.value];
        }
    }
    return (locationPage || detailsPage) && payload.dealers?.length;
};

/**
 * Build the structured data
 *
 * @param SeoProps
 * @returns Jsonld
 */
const json = ({ state, fields, payload, url }: SeoProps): Jsonld => {
    const dealerDetailsUrl = getPageLinkByPageType(state, "DealerDetailsPage")?.url;
    return {
        "@context": "https://schema.org",
        "@graph": payload.dealers?.map((item: any, index: number) => {
            const seoObject: any = {
                "@context": "https://schema.org",
                "@type": "LocalBusiness",
                name: item.name,
                telephone: item.phone,
                url: `${url + dealerDetailsUrl}?id=${item.id}`,
                image: fields.logoUrl,
                address: {
                    "@type": "PostalAddress",
                    addressLocality: `${item.city} ${item.state}`,
                    postalCode: item.postalCode,
                    streetAddress: `${item.address1} ${item.address2}`,
                },
                geo: {
                    "@type": "GeoCoordinates",
                    latitude: item.latitude,
                    longitude: item.longitude,
                },
            };
            if (item.htmlContent) {
                seoObject.description = item.htmlContent.replace(/(<([^>]+)>)/gi, "");
            }
            return seoObject;
        }),
    };
};

export default { activate, json };
